import { z } from "zod";

/**
 * Sprint 3 Workflow Bundle #2 — Reflex testing rules.
 *
 * A reflex rule reacts to a lab value's computed abnormal_flag by
 * auto-creating a new pending lab_order with follow-up tests. Classic
 * example: TSH out-of-range → auto-order FT4 + FT3.
 *
 * The condition vocabulary mirrors the abnormal_flag enum + a fan-in
 * 'any_abnormal' bucket. Direction-sensitive rules ('low'/'high') let
 * a clinic respond differently to high vs low — useful when the
 * follow-up battery differs (low Hb → iron studies, high Hb → hematocrit).
 */

export const reflexConditionSchema = z.enum([
	"low",
	"high",
	"critical_low",
	"critical_high",
	"abnormal",
	"any_abnormal",
]);
export type ReflexCondition = z.infer<typeof reflexConditionSchema>;

export const clinicReflexRuleSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	source_test_name: z.string(),
	condition: reflexConditionSchema,
	triggered_tests: z.array(z.string()),
	is_active: z.boolean(),
	notes: z.string().nullable(),
	created_by_member_id: z.string().uuid().nullable(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type ClinicReflexRuleApi = z.infer<typeof clinicReflexRuleSchema>;

export const createReflexRuleInputSchema = z.object({
	source_test_name: z.string().min(1).max(200),
	condition: reflexConditionSchema,
	triggered_tests: z.array(z.string().min(1).max(200)).min(1).max(20),
	notes: z.string().max(2000).nullable().optional(),
});
export type CreateReflexRuleInput = z.infer<typeof createReflexRuleInputSchema>;

export const updateReflexRuleInputSchema = z.object({
	source_test_name: z.string().min(1).max(200).optional(),
	condition: reflexConditionSchema.optional(),
	triggered_tests: z
		.array(z.string().min(1).max(200))
		.min(1)
		.max(20)
		.optional(),
	is_active: z.boolean().optional(),
	notes: z.string().max(2000).nullable().optional(),
});
export type UpdateReflexRuleInput = z.infer<typeof updateReflexRuleInputSchema>;
